import { Component, OnInit, OnChanges, OnDestroy, Input, ViewChild, Output, EventEmitter } from '@angular/core';
// ngrx store
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
// services
import { CollectionService } from '../../../services/collection.service';
import { GeocodeService } from '../../../services/map-geocode.service';
import { EventsService } from '../../../services/events.service';
// models
import { AppState } from '../../../models/app-state.interface';
import { BookingInitState } from '../../../models/booking-reducer.interface';
import { CollectionLocation } from '../../../models/collection.interface';
import { GetLocationItem } from '../../../models/get-location.interface';
// globals
import { COUNTRY_CODES } from '../../../../shared/globals';
import { SharedService } from '../../../../shared/services/shared-service.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LocationResidentialLookupComponent } from '@shared/components/location-residential-lookup/location-residential-lookup.component';

@Component({
  selector: 'app-billing-address',
  standalone: true,
  imports: [CommonModule, FormsModule, LocationResidentialLookupComponent],
  templateUrl: './billing-address.component.html',
  styleUrls: ['./billing-address.component.scss'],
  providers: [GeocodeService]
})
export class BillingAddressComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  billingAddressValid;
  @Input() appCopy;
  @Input() selectedLanguage;
  @Input() formData;

  @Output()
  selectedGeoCoords: EventEmitter<any> = new EventEmitter();

  @Output()
  handleShowMenu: EventEmitter<any> = new EventEmitter();

  @ViewChild('form', { static: true }) form;

  private _locationSubscription: Subscription;
  private _geoCoordsValidationSubscription: Subscription;
  private _formChangesSubscription: Subscription;
  private _childPrediction = '';
  private _locationArr = [];
  private _ind = 0;
  private departureInfo;
  private arrivalInfo;
  private bookingProduct;
  private _lastEnteredValue: string;
  private _timeout;
  private _updatedPostcodeValue;

  public currentBooking;
  public bookingState: BookingInitState;
  public selectedAddressLine = [''];
  public showManualInput = false;
  public errorNoPostcode = false;
  public errorPostcodeNotEntered = false;
  public formHasErrors = false;
  public countries = COUNTRY_CODES;
  public postcodeInZone = true;
  public userAddresses: Array<any>;
  public postcodeCheck: boolean = false;
  public postcodeInvalid: boolean = false;
  public locationData: CollectionLocation = {
    type: 4,
    name: '',
    shortName: '',
    geoCoord: null,
    addressLine1: '',
    addressLine2: '',
    addressTown: '',
    addressPostCode: '',
    addressCounty: '',
    addressCountry: 'United Kingdom'
  };
  public addressplaceHolder = 'Search for your billing address';
  public isIInvalidaCollectionAddressLine1 = false;
  public isInvalidCollectionAddressLine2 = false;
  public isInvalidCollectionAddressPostcode = false;
  public isInvalidCollectionAddressTown = false;
  public isValidCollectionAddressCounty = false;
  public isInvalidCollectionAddressCountry = false;
  public countryCodes = COUNTRY_CODES.all;

  constructor(
    private _appStore: Store<AppState>,
    private _collectionService: CollectionService,
    private _geocodeService: GeocodeService,
    private _sharedService: SharedService,
    private _eventsService: EventsService
  ) { }

  ngOnInit() {
    const currentAppState = this.getState(this._appStore);
    // user saved addresses
    this.userAddresses = currentAppState.userReducer.userDetails.userAddresses;
    const flightData = currentAppState.flightReducer.flightDetails.flightResponse;
    this.bookingProduct = currentAppState.bookingReducer.bookingDetails.bookingProduct;
    this.departureInfo = flightData.flightStatusDetails[0].departureAirport;
    this.arrivalInfo = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport;
    this.currentBooking = currentAppState.currentBookingReducer.currentBooking;
    this.bookingState = currentAppState.bookingReducer;

    // listen to form changes
    this._formChangesSubscription = this.form.form.valueChanges.subscribe(x => {
      this.formHasErrors = false;
    })

    // subscribe to location data sent from google places
    this._locationSubscription = this._collectionService.locationSubject$
      .subscribe(loc => {
          this._ind ++;
          this._locationArr = [];
          // if response returns error
          // ignore response
          if (loc === 'error') {
            this.locationData.addressLine1 = this._childPrediction;
            this._locationArr.push(this.locationData.addressLine1, this._ind);
            this.selectedAddressLine = this._locationArr;
            return;
          }

          // api response error
          if (loc.error) {
            // // console.log('location error', loc);
            return;
          }

          // if no postcode
          // or postcode is not in postcode array
          // throw error
          if (loc[0] === 'empty postcode') {
            this.errorNoPostcode = true;
            return;
          } else if (loc === 'no place id') {
            this.errorPostcodeNotEntered = true;
            return;
          }
          this.locationData = loc.location;

          this._locationArr.push(this.locationData.addressLine1, this._ind);
          this.selectedAddressLine = this._locationArr;

          this.locationData.name = '';

          this.showManualInput = true;
      });

      this._geoCoordsValidationSubscription = this._collectionService.geoCoordsSubject$
      .subscribe(
        e => {
          if (e.isValid && e.isCalled) {
            this.postcodeInZone = true;
            this.postcodeInvalid = false;
          } else {
            this.postcodeInZone = false;
            this.postcodeInvalid = true;
          }
        });

    if (this.formData) {
      this.locationData.addressLine1 = this.formData.houseNumberOrName;
      this.locationData.addressLine2 = this.formData.street;
      this.locationData.addressPostCode = this.formData.postalCode;
      this.locationData.addressTown = this.formData.city;
      this.locationData.addressCounty = this.formData.stateOrProvince;
      this.locationData.addressCountry = this.countryCodes.find(item => item.countryName === this.formData.country || item.countryCodeName === this.formData.country).countryName;
    }
  }

  ngOnChanges(changes: any) {
    if (changes.billingAddressValid) {
      this.billingAddressValid = changes.billingAddressValid.currentValue;

      if (this.billingAddressValid === false) {
        this.checkFormValid(this.form);
        document.body.scrollTop = 0;
      }
    }
    if (this.selectedLanguage || this.selectedLanguage === '') {
      this.addressplaceHolder = this.appCopy.panelHeader['manualAddressPlaceholder'+this.selectedLanguage]
    }
  }

  getGeocode(postcode) {
    this._geocodeService.getGeocode(postcode, 'CountryCode');
  }

  clearInput() {
    this.locationData = {
      type: 4,
      name: '',
      shortName: '',
      geoCoord: null,
      addressLine1: '',
      addressLine2: '',
      addressTown: '',
      addressPostCode: '',
      addressCounty: '',
      addressCountry: ''
    };
    this.selectedAddressLine = [''];
    this.showManualInput = false;
    this.postcodeInvalid = false;
    this.changedAddressLine('');
  }

  selectedSavedLocationResidential(event) {
  }

  toggleShowMenu(event) {
    this.handleShowMenu.emit(event);
  }

  changedAddressLine(event) {
    this.locationData.addressLine1 = event;
    this.errorNoPostcode = false;
    this.formHasErrors = false;
  }

  // type ahead address search
  // from allies api
  // component location-residential-lookup
  getChildLocation(event) {

    this.resetValidation();
    this._childPrediction = event.prediction;
    let airportCode;
    const request: GetLocationItem = {
      locationProvider: 3,
      locationType: 4,
      searchId: event.selectedItem.Id,
      partialSearch: event.selectedItem.Text + event.selectedItem.Description,
      airportCode: this.bookingProduct === 'abc' ? this.departureInfo.airportCode : this.arrivalInfo.airportCode
    };
    // if (this.bookingProduct === 'abc') {
    //   airportCode = this.departureInfo.airportCode;
    // } else {
    //   airportCode = this.arrivalInfo.airportCode;
    // }
    this._collectionService.getLocationByProvider(request , true);
  }

  resetValidation() {
    this.errorNoPostcode = false;
    this.formHasErrors = false;
  }

  addressChaneHandler(event) {
    const form = this.form;

        if (form.value && form.value.collectionAddressLine1.length > 0 && form.value.collectionAddressLine1.trim() === '') {
            form.controls.collectionAddressLine1.markAsDirty();
            this.isIInvalidaCollectionAddressLine1 = true;
          } else {
            this.isIInvalidaCollectionAddressLine1 = false;
        }

        if (form.value && form.value.collectionAddressLine2.length > 0 && form.value.collectionAddressLine2.trim() === '') {
          form.controls.collectionAddressLine2.markAsDirty();
          this.isInvalidCollectionAddressLine2 = true;
        } else {
          this.isInvalidCollectionAddressLine2 = false;
        }

        if (form.value && form.value.collectionAddressPostcode.length > 0 && form.value.collectionAddressPostcode.trim() === '') {
          form.controls.collectionAddressPostcode.markAsDirty();
          this.isInvalidCollectionAddressPostcode = true;
        } else {
          this.isInvalidCollectionAddressPostcode = false;
        }

        if (form.value && form.value.collectionAddressTown.length > 0 && form.value.collectionAddressTown.trim() === '') {
          form.controls.collectionAddressTown.markAsDirty();
          this.isInvalidCollectionAddressTown = true;
        } else {
          this.isInvalidCollectionAddressTown = false;
        }

        if (form.value && form.value.collectionAddressCounty.length > 0 && form.value.collectionAddressCounty.trim() === '') {
          form.controls.collectionAddressCounty.markAsDirty();
          this.isValidCollectionAddressCounty = true;
        } else {
          this.isValidCollectionAddressCounty = false;
        }

        if (form.value && form.value.collectionAddressCountry.trim() === '') {
          form.controls.collectionAddressCountry.markAsDirty();
          this.isInvalidCollectionAddressCountry = true;
        } else {
          this.isInvalidCollectionAddressCountry = false;
        }
  }

  checkFormValid(form) {

    this.resetValidation();

    if (form.value) {
      if (Object.keys(form.value).length === 0) {
        this.formHasErrors = true;
      } else {

        if (form.value.collectionAddressLine1.trim() === '') {
          form.controls.collectionAddressLine1.markAsDirty();
          this.formHasErrors = true;
        }
        if (form.value.collectionAddressLine2.trim() === '') {
          form.controls.collectionAddressLine2.markAsDirty();
          this.formHasErrors = true;
        }
        if (form.value.collectionAddressTown.trim() === '') {
          form.controls.collectionAddressTown.markAsDirty();
          this.formHasErrors = true;
        }
        if (form.value.collectionAddressPostcode.trim() === '') {
          form.controls.collectionAddressPostcode.markAsDirty();
          this.formHasErrors = true;
        }

        if (form.value.collectionAddressCounty.trim() === '') {
          form.controls.collectionAddressCounty.markAsDirty();
          this.formHasErrors = true;
        }
        if (form.value.collectionAddressCountry.trim() === '') {
          form.controls.collectionAddressCountry.markAsDirty();
          this.formHasErrors = true;
        }
      }
    }

    return !this.formHasErrors;
  }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1)).subscribe(s => state = s);
    return state;
  }

  ngOnDestroy() {
    if (this._locationSubscription) {
      this._locationSubscription.unsubscribe();
    }
    if (this._formChangesSubscription) {
      this._formChangesSubscription.unsubscribe();
    }
    this._geoCoordsValidationSubscription.unsubscribe();
  }

}
